// src/services/capturasVuelo.js
// Convierte lo que se leyó de las capturas (Comprarviagem, Speedmilhas, Resumo do pedido,
// Buscamilhas) en datos de filas del Cotizador. Funciones puras: la lectura con IA vive en
// netlify/functions/extract-flight-cotizador.js y aquí solo se ordena el resultado.
//
// Reglas:
//  · Cada captura es UN vuelo. Capturas del mismo vuelo (mismo origen, horas y aerolínea) se
//    juntan en una sola fila, cada precio en su casilla. Vuelos distintos = filas distintas.
//  · Nada se inventa: un dato que no se leyó no se toca.
//  · Speedmilhas no trae fecha: se usa el día de la fila.
import { CIUDADES } from './milhasCalc';
import { fmtBRL, diaNum, isoDeDiaNum } from './cotizadorCalc';
import { normalizarAerolinea, etiquetaAeropuerto } from './aeropuertos';

const num = (v) => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? null : Number(v));
const hhmm = (v) => (typeof v === 'string' && /^\d{2}:\d{2}$/.test(v) ? v : null);
const iata = (v) => (typeof v === 'string' && /^[A-Za-z]{3}$/.test(v.trim()) ? v.trim().toUpperCase() : null);

// Qué fuente es la captura. Si no se sabe, no se adivina.
export const TIPOS_CAPTURA = ['comprarviagem', 'speedmilhas', 'resumen_pedido', 'buscamilhas'];
const NOMBRE_FUENTE = {
  comprarviagem: 'Comprarviagem', speedmilhas: 'Speedmilhas', resumen_pedido: 'Resumen del pedido', buscamilhas: 'Buscamilhas (millas)',
};

// ¿Dos capturas son del mismo vuelo? Se juntan si:
//  · tienen el mismo número de vuelo (siempre), o
//  · coincide la hora de salida o la de llegada y NINGÚN dato se contradice (origen, horas,
//    aerolínea). Que a una captura le falte un dato (por ejemplo recortada) no impide juntarlas:
//    solo cuenta lo que está en las dos.
const numeroVuelo = (l) => String(l.flight_number || '').replace(/\s+/g, '').toUpperCase() || null;
const aerolineaDe = (l) => normalizarAerolinea(l.airline).toLowerCase() || null;
const campos = (l) => ({
  num: numeroVuelo(l), origen: iata(l.origin_code), sale: hhmm(l.departure_time), llega: hhmm(l.arrival_time), aero: aerolineaDe(l),
});
// Perfil de un grupo: el primer valor leído de cada dato.
const perfilDe = (lecturas) => lecturas.map(campos).reduce((p, c) => ({
  num: p.num || c.num, origen: p.origen || c.origen, sale: p.sale || c.sale, llega: p.llega || c.llega, aero: p.aero || c.aero,
}), { num: null, origen: null, sale: null, llega: null, aero: null });

export function mismoVuelo(perfilGrupo, lectura) {
  const a = perfilGrupo;
  const b = campos(lectura);
  if (a.num && b.num) return a.num === b.num; // mismo número = mismo vuelo; distinto = otro vuelo
  const choca = (x, y) => x && y && x !== y;
  if (choca(a.origen, b.origen) || choca(a.sale, b.sale) || choca(a.llega, b.llega) || choca(a.aero, b.aero)) return false;
  return !!((a.sale && b.sale) || (a.llega && b.llega));
}

export const claveVuelo = (l) => { const c = campos(l); return [c.origen || '', c.sale || '', c.llega || '', c.aero || ''].join('|'); };

// Cambio de aeropuerto entre tramos: "llega a GRU y el siguiente sale de CGH".
export function cambiosDeAeropuerto(legs) {
  const l = (Array.isArray(legs) ? legs : []).map((x) => ({ from: iata(x?.from), to: iata(x?.to) }));
  const out = [];
  for (let i = 0; i < l.length - 1; i += 1) {
    if (l[i].to && l[i + 1].from && l[i].to !== l[i + 1].from) out.push({ llega: l[i].to, sale: l[i + 1].from });
  }
  return out;
}

// Fecha del vuelo que dice una captura, con el año SIEMPRE comprobado. Las capturas muchas veces no
// traen año ("5 nov.", o ninguna fecha), y cuando la IA lo suponía salía 2024 o 2025. Reglas:
//  · el año de la captura solo vale si es el de la fila o el siguiente; si no, se usa el de la fila;
//  · si solo hay día y mes, se usa el año de la fila (o el siguiente si quedaría más de 6 meses antes).
// Devuelve { iso, avisoAnio } o null si la captura no trae fecha.
export function fechaDeCaptura(l, diaFila) {
  const filaN = diaNum(diaFila);
  if (filaN === null) return null;
  const anioFila = Number(String(diaFila).slice(0, 4));
  let dm = null;
  let anioLeido = null;
  if (typeof l.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(l.date)) { anioLeido = Number(l.date.slice(0, 4)); dm = l.date.slice(5); }
  else if (typeof l.date_day_month === 'string' && /^\d{2}-\d{2}$/.test(l.date_day_month)) dm = l.date_day_month;
  if (!dm) return null;
  const candidata = (y) => { const iso = `${y}-${dm}`; const n = diaNum(iso); return n !== null && isoDeDiaNum(n) === iso ? iso : null; };
  const valido = anioLeido === anioFila || anioLeido === anioFila + 1;
  let iso = candidata(valido ? anioLeido : anioFila);
  if (!iso) return null;
  if (!valido && diaNum(iso) < filaN - 180) iso = candidata(anioFila + 1) || iso;
  return { iso, avisoAnio: anioLeido !== null && !valido ? anioLeido : null };
}

// lecturas: [{ capture_type, airline, origin_code, destination_code, date, departure_time, arrival_time,
//   arrival_day_offset, stops, flight_number, legs, price_adult, price_total, miles, taxa, multiple, error }]
export function agruparLecturas(lecturas) {
  const grupos = new Map();
  const sinLeer = [];
  (lecturas || []).forEach((l, i) => {
    if (!l || l.error || (!hhmm(l.departure_time) && !hhmm(l.arrival_time) && !numeroVuelo(l))) { sinLeer.push(i + 1); return; }
    let g = [...grupos.values()].find((x) => mismoVuelo(perfilDe(x.lecturas), l));
    if (!g) {
      g = { clave: `vuelo-${grupos.size + 1}`, lecturas: [], fuentes: [], avisos: [] };
      grupos.set(g.clave, g);
    }
    g.lecturas.push(l);
    if (l.multiple) g.avisos.push('La captura mostraba varios vuelos: se leyó solo el primero.');
  });
  return { grupos: [...grupos.values()], sinLeer };
}

// Primer valor no nulo de un campo entre las lecturas del grupo.
const primero = (g, campo, ok = (v) => v !== null && v !== undefined && v !== '') => {
  const l = g.lecturas.find((x) => ok(x[campo]));
  return l ? l[campo] : null;
};

// Datos de la fila para un grupo de lecturas. `diaFila` = día de la fila donde se está cargando.
// Devuelve { patch, resumen: [texto], avisos: [texto], precios: [{ fuente, valor }] }.
export function datosDeGrupo(g, diaFila) {
  const patch = {};
  const resumen = [];
  const avisos = [...g.avisos];
  const precios = [];

  const origen = iata(primero(g, 'origin_code', iata));
  if (origen === 'BVB' || origen === 'MAO') patch.sale_de = origen;
  else if (origen) avisos.push(`El vuelo sale de ${origen}, no de Manaus ni de Boa Vista: revisa la fila.`);

  // Si las capturas del mismo vuelo no coinciden en una hora, no se elige: se deja vacía y se avisa.
  const unico = (campo, nombre) => {
    const vals = [...new Set(g.lecturas.map((l) => hhmm(l[campo])).filter(Boolean))];
    if (vals.length > 1) { avisos.push(`Las capturas no coinciden en la hora de ${nombre} (${vals.join(' / ')}): ponla tú.`); return null; }
    return vals[0] || null;
  };
  const sale = unico('departure_time', 'salida');
  const llega = unico('arrival_time', 'llegada');
  if (sale) patch.hora_vuelo = sale;
  if (llega) patch.hora_llegada_vuelo = llega;
  const off = num(primero(g, 'arrival_day_offset', (v) => num(v) !== null));
  if (off !== null && off >= 0 && off <= 3) patch.llegada_mas_dias = Math.floor(off);

  const aerolinea = normalizarAerolinea(primero(g, 'airline'));
  if (aerolinea) patch.aerolinea = aerolinea;
  const escalas = num(primero(g, 'stops', (v) => num(v) !== null));
  if (escalas !== null && escalas >= 0) patch.escalas = Math.floor(escalas);
  const numero = primero(g, 'flight_number');
  if (numero) patch.numero_vuelo = String(numero).trim().toUpperCase();

  const destino = iata(primero(g, 'destination_code', iata));
  if (destino) {
    if (CIUDADES[destino]) patch.ciudad = CIUDADES[destino];
    patch.aeropuerto_llegada = etiquetaAeropuerto(destino);
  }
  if (origen) patch.aeropuerto_salida = etiquetaAeropuerto(origen);

  // Día del vuelo: si la captura trae una fecha distinta a la de la fila, va en una fila de ese día.
  // El año se comprueba (ver fechaDeCaptura): nunca se crea una fila en un año que no corresponde.
  const fechas = g.lecturas.map((l) => fechaDeCaptura(l, diaFila)).filter(Boolean);
  const unicas = [...new Set(fechas.map((f) => f.iso))];
  if (unicas.length > 1) {
    avisos.push(`Las capturas no coinciden en el día del vuelo (${unicas.map((f) => `${f.slice(8)}/${f.slice(5, 7)}`).join(' / ')}): revísalo.`);
  } else if (unicas.length === 1 && unicas[0] !== diaFila) {
    const f = unicas[0];
    patch.dia_vuelo = f;
    avisos.push(`La captura es del ${f.slice(8)}/${f.slice(5, 7)}/${f.slice(0, 4)}, no del día de la fila: va en una fila de ese día.`);
  }
  const anioIgnorado = fechas.find((f) => f.avisoAnio);
  if (anioIgnorado) avisos.push(`La captura decía el año ${anioIgnorado.avisoAnio}: no es creíble y se usó el de la fila (${String(diaFila).slice(0, 4)}). Revisa la fecha.`);

  // Precios, cada uno en su casilla según de qué sitio es la captura.
  // Si dos capturas del mismo sitio traen precios distintos para el mismo vuelo, se queda el menor
  // y se avisa con los dos valores.
  const poner = (clave, tipo, valor) => {
    const previo = patch[clave];
    if (previo !== undefined && previo !== valor) {
      avisos.push(`Dos capturas de ${NOMBRE_FUENTE[tipo]} traen precios distintos (R$ ${fmtBRL(previo)} y R$ ${fmtBRL(valor)}): se usó el menor, revísalo.`);
    }
    patch[clave] = previo === undefined ? valor : Math.min(previo, valor);
    precios.push({ fuente: NOMBRE_FUENTE[tipo], valor });
  };
  g.lecturas.forEach((l) => {
    const tipo = TIPOS_CAPTURA.includes(l.capture_type) ? l.capture_type : null;
    const adulto = num(l.price_adult);
    const total = num(l.price_total);
    if (tipo === 'comprarviagem' && adulto !== null) poner('comprarviagem_lista', tipo, adulto);
    else if (tipo === 'speedmilhas' && total !== null) poner('speedmilhas', tipo, total);
    else if (tipo === 'resumen_pedido' && total !== null) poner('resumen_pedido', tipo, total);
    else if (tipo === 'buscamilhas' && num(l.miles) !== null && num(l.miles) >= 5000) {
      patch.millas_cantidad = num(l.miles);
      if (num(l.taxa) !== null) patch.taxa = num(l.taxa);
      precios.push({ fuente: NOMBRE_FUENTE[tipo], valor: null, millas: num(l.miles) });
    } else if (adulto !== null || total !== null) {
      avisos.push('Hay un precio en una captura que no pude identificar (de qué sitio es): ponlo a mano.');
    }
  });

  // Cambio de aeropuerto entre vuelos (por ejemplo llega a Guarulhos y el siguiente sale de Congonhas).
  const cambios = g.lecturas.flatMap((l) => cambiosDeAeropuerto(l.legs));
  if (cambios.length) {
    const texto = cambios.map((c) => `${c.llega} → ${c.sale}`).join(', ');
    avisos.push(`Cambio de aeropuerto: llega a ${cambios[0].llega} y el siguiente vuelo sale de ${cambios[0].sale}. Consulta quién lo resuelve antes de vender.`);
    patch.nota = `Cambio de aeropuerto ${texto}`;
  }

  if (sale || llega) resumen.push(`${origen || '?'} ${sale || '--:--'} → ${destino || '?'} ${llega || '--:--'}${off ? ` (+${off})` : ''}`);
  if (aerolinea || numero) resumen.push([aerolinea, numero].filter(Boolean).join(' · '));
  if (escalas !== null) resumen.push(escalas === 0 ? 'directo' : `${escalas} ${escalas === 1 ? 'escala' : 'escalas'}`);
  return { patch, resumen, avisos: [...new Set(avisos)], precios };
}

const vacio = (v) => v === null || v === undefined || v === '';

// Una fila está libre para llenarla con capturas si todavía no tiene ningún vuelo (ni hora de
// salida, ni de llegada, ni aerolínea). Que ya tenga precios escritos a mano no la ocupa: es lo
// normal cuando se escribe un precio y después se suben las capturas.
export function filaSinVuelo(op) {
  return ['hora_vuelo', 'hora_llegada_vuelo', 'aerolinea'].every((k) => vacio(op[k]));
}
// Fila totalmente vacía (sin vuelo ni precios).
export function filaVacia(op) {
  return filaSinVuelo(op) && ['comprarviagem_lista', 'speedmilhas', 'resumen_pedido', 'millas_cantidad'].every((k) => vacio(op[k]));
}

const PRECIOS_FILA = [
  ['comprarviagem_lista', 'Comprarviagem'], ['speedmilhas', 'Speedmilhas'], ['resumen_pedido', 'Resumen del pedido'], ['millas_cantidad', 'Millas'],
];

// Reparte los grupos: el primero llena la fila actual si no tiene vuelo; el resto, filas nuevas.
// Un grupo cuya captura es de otro día siempre va a una fila nueva. Para el que llena la fila
// actual se dice qué precios ya escritos se reemplazan y cuáles se conservan.
export function planDeFilas(grupos, opActual) {
  const datos = grupos.map((g) => ({ g, ...datosDeGrupo(g, opActual.dia_vuelo) }));
  let usadaActual = !filaSinVuelo(opActual);
  return datos.map((d) => {
    const otroDia = !!d.patch.dia_vuelo;
    const enActual = !usadaActual && !otroDia;
    if (enActual) usadaActual = true;
    const reemplazos = [];
    const conservados = [];
    if (enActual) {
      PRECIOS_FILA.forEach(([k, nombre]) => {
        const antes = num(opActual[k]);
        if (antes === null) return;
        if (d.patch[k] === undefined) conservados.push({ campo: k, nombre, valor: antes });
        else if (d.patch[k] !== antes) reemplazos.push({ campo: k, nombre, antes, despues: d.patch[k] });
      });
    }
    return { ...d, enActual, reemplazos, conservados };
  });
}
